import React, { useState } from 'react';
import axios from 'axios';
import { collection, doc, getDocs, setDoc, writeBatch } from 'firebase/firestore';
import { Upload, FileText, X, CheckCircle, AlertCircle } from 'lucide-react';
import { auth, db } from '../../firebase';
import { EcointelLottieMark } from '../EcoIntelLoader';
import { useUserFacingError } from '../../hooks/useUserFacingError';

/**
 * Admin modal: parses a GEE config script and either
 *  - imports a new property (mode='import') from an EE asset, or
 *  - syncs config metadata onto an existing property (mode='sync').
 *
 * Behaviour preserved verbatim from the original AdminDashboard.js — only
 * the file location changed so the admin sub-routes can mount it from the
 * new PropertiesAdmin page without dragging the rest of the monolith.
 */
export default function ConfigUploadModal({ mode, assetId, farmId, onClose, onComplete, serverUrl }) {
  const { formatError, logWarn } = useUserFacingError();
  const [step, setStep] = useState('upload');
  const [configFile, setConfigFile] = useState(null);
  const [parsedConfig, setParsedConfig] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const expectedName = mode === 'import' ? (assetId || '').split('/').pop() : farmId;

  const handleParse = async () => {
    if (!configFile) return;
    setLoading(true);
    setError(null);
    try {
      const text = await configFile.text();
      const token = await auth.currentUser.getIdToken();
      const res = await axios.post(`${serverUrl}/parse-property-config`,
        { configText: text },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const parsed = res.data;
      if (!parsed.parcels || parsed.parcels.length === 0) {
        setError('Config file contains no parcels.');
        setLoading(false);
        return;
      }
      if (!parsed.asset_folder) {
        setError('Config file does not define an asset_folder.');
        setLoading(false);
        return;
      }
      setParsedConfig(parsed);
      setStep('preview');
    } catch (err) {
      setError(`Parse failed: ${formatError(err)}`);
    }
    setLoading(false);
  };

  const handleConfirm = async () => {
    if (!parsedConfig) return;
    setLoading(true);
    setError(null);
    try {
      const token = await auth.currentUser.getIdToken();
      const targetFarmId = mode === 'import' ? (parsedConfig.asset_folder || assetId.split('/').pop()) : farmId;

      if (mode === 'import') {
        await setDoc(doc(db, 'farms', targetFarmId), {
          id: targetFarmId,
          farmName: parsedConfig.property_name || targetFarmId,
          status: 'READY',
          source: 'GEE',
          geeAssetId: assetId,
          propertyId: parsedConfig.property_id || null,
          province: parsedConfig.province || null,
          jurisdiction: parsedConfig.jurisdiction || null,
          coordinates: parsedConfig.coordinates || null,
          configImportedAt: new Date().toISOString(),
          parcelCount: parsedConfig.parcels.length,
          lastUpdate: new Date().toISOString()
        });
      } else {
        await setDoc(doc(db, 'farms', targetFarmId), {
          farmName: parsedConfig.property_name || undefined,
          propertyId: parsedConfig.property_id || undefined,
          province: parsedConfig.province || undefined,
          jurisdiction: parsedConfig.jurisdiction || undefined,
          coordinates: parsedConfig.coordinates || undefined,
          configImportedAt: new Date().toISOString(),
          parcelCount: parsedConfig.parcels.length,
        }, { merge: true });
      }

      const batch = writeBatch(db);
      for (const parcel of parsedConfig.parcels) {
        const parcelRef = doc(db, 'farms', targetFarmId, 'parcels', parcel.id);
        const displayName = parcel.name || parcel.farm_field_name || parcel.id;
        batch.set(parcelRef, {
          id: parcel.id,
          parcel_id: parcel.id,
          displayName,
          name: parcel.name || null,
          farm_field_name: parcel.farm_field_name || null,
          type: parcel.type || null,
          area_ha: parcel.area_ha != null ? Number(parcel.area_ha) : null,
          perimeter_m: parcel.perimeter_m != null ? Number(parcel.perimeter_m) : null,
          centroid: parcel.centroid || null,
          os_field_ref: parcel.os_field_ref || null,
          os_sheet_id: parcel.os_sheet_id || null,
          os_parcel_id: parcel.os_parcel_id || null,
          sbi: parcel.sbi || null,
          lfa_code: parcel.lfa_code || null,
          created_on: parcel.created_on || null,
          ...(parcel.calibrate !== undefined ? { calibrate: parcel.calibrate } : {}),
          ...(parcel.aggregate !== undefined ? { aggregate: parcel.aggregate } : {}),
          ...(parcel.include_in_site_aggregate !== undefined
            ? { include_in_site_aggregate: parcel.include_in_site_aggregate }
            : {}),
          source: 'config',
          configSyncedAt: new Date().toISOString(),
        }, { merge: true });
      }
      await batch.commit();

      const cacheSnap = await getDocs(collection(db, 'farms', targetFarmId, 'cache'));
      if (cacheSnap.size > 0) {
        const cacheBatch = writeBatch(db);
        cacheSnap.docs.forEach(d => cacheBatch.delete(d.ref));
        await cacheBatch.commit();
      }

      try {
        const token = await auth.currentUser.getIdToken(true);
        await axios.post(`${serverUrl}/clear-cache`,
          { farmId: targetFarmId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (e) {
        logWarn('Cache clear failed (non-critical)', e);
      }

      setStep('done');
      setTimeout(() => {
        onComplete(targetFarmId);
      }, 1500);
    } catch (err) {
      setError(`${mode === 'import' ? 'Import' : 'Sync'} failed: ${formatError(err)}`);
    }
    setLoading(false);
  };

  const totalArea = parsedConfig?.parcels?.reduce((sum, p) => sum + (p.area_ha || 0), 0) || 0;
  const folderMismatch = parsedConfig && expectedName && parsedConfig.asset_folder !== expectedName;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl p-6 max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-lg">
            {mode === 'import' ? 'Import Property with Config' : 'Upload Config to Property'}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>

        {mode === 'import' && (
          <div className="text-sm text-slate-500 mb-4">
            EE Asset: <span className="font-mono text-xs bg-slate-100 px-2 py-0.5 rounded">{(assetId || '').split('/').pop()}</span>
          </div>
        )}
        {mode === 'sync' && (
          <div className="text-sm text-slate-500 mb-4">
            Property: <span className="font-mono text-xs bg-slate-100 px-2 py-0.5 rounded">{farmId}</span>
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-start gap-2">
            <AlertCircle size={16} className="mt-0.5 shrink-0" /> {error}
          </div>
        )}

        {step === 'upload' && (
          <div className="space-y-4">
            <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center hover:border-[#043132] transition-colors">
              <Upload size={32} className="mx-auto mb-2 text-slate-400" />
              <p className="text-sm text-slate-600 mb-2">Select a GEE config script</p>
              <p className="text-xs text-slate-400 mb-3">e.g. config_tovoforest, config_dhtfarm</p>
              <label className="inline-block cursor-pointer bg-[#043132] text-white text-sm px-4 py-2 rounded-lg hover:bg-[#065354] transition-colors">
                Choose File
                <input
                  type="file"
                  className="hidden"
                  onChange={e => { setConfigFile(e.target.files[0]); setError(null); }}
                />
              </label>
              {configFile && (
                <div className="mt-3 flex items-center justify-center gap-2 text-sm text-slate-700">
                  <FileText size={14} /> {configFile.name}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="px-4 py-2 text-slate-500 hover:text-slate-700 text-sm font-medium">Cancel</button>
              <button
                onClick={handleParse}
                disabled={!configFile || loading}
                className="px-5 py-2 bg-[#043132] text-white rounded-lg hover:bg-[#065354] font-medium text-sm transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {loading ? <><EcointelLottieMark size={14} /> Parsing...</> : 'Parse Config'}
              </button>
            </div>
          </div>
        )}

        {step === 'preview' && parsedConfig && (
          <div className="space-y-4">
            {folderMismatch && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                Config asset_folder "<span className="font-mono">{parsedConfig.asset_folder}</span>" does not match
                expected "<span className="font-mono">{expectedName}</span>".
                {mode === 'sync' ? ' Proceeding will sync parcels into this property regardless.' : ' The property will be created using the config asset_folder.'}
              </div>
            )}

            <div className="bg-slate-50 rounded-lg p-4 space-y-2">
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                <span className="text-slate-500">Property Name</span>
                <span className="font-medium">{parsedConfig.property_name || '—'}</span>
                <span className="text-slate-500">Property ID</span>
                <span className="font-mono text-xs">{parsedConfig.property_id || '—'}</span>
                <span className="text-slate-500">Asset Folder</span>
                <span className="font-mono text-xs">{parsedConfig.asset_folder || '—'}</span>
                <span className="text-slate-500">Location</span>
                <span>{[parsedConfig.province, parsedConfig.jurisdiction].filter(Boolean).join(', ') || '—'}</span>
                <span className="text-slate-500">Parcels</span>
                <span className="font-medium">{parsedConfig.parcels.length}</span>
                <span className="text-slate-500">Total Area</span>
                <span className="font-medium">{totalArea.toFixed(2)} ha</span>
              </div>
            </div>

            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <div className="bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider border-b">
                Parcels ({parsedConfig.parcels.length})
              </div>
              <div className="max-h-48 overflow-y-auto divide-y divide-slate-100">
                {parsedConfig.parcels.map(p => (
                  <div key={p.id} className="px-3 py-2 flex items-center justify-between text-sm">
                    <div className="flex-1 min-w-0">
                      <span className="font-medium text-slate-800">{p.name || p.id}</span>
                      <span className="text-xs text-slate-400 ml-2">{p.id}</span>
                    </div>
                    <div className="flex items-center gap-3 shrink-0 text-xs text-slate-500">
                      <span>{p.type || '—'}</span>
                      {p.area_ha != null && <span>{p.area_ha.toFixed(2)} ha</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <button onClick={() => { setStep('upload'); setParsedConfig(null); setError(null); }} className="px-4 py-2 text-slate-500 hover:text-slate-700 text-sm font-medium">Back</button>
              <button
                onClick={handleConfirm}
                disabled={loading}
                className="px-5 py-2 bg-[#043132] text-white rounded-lg hover:bg-[#065354] font-medium text-sm transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {loading ? <><EcointelLottieMark size={14} /> {mode === 'import' ? 'Importing...' : 'Syncing...'}</> :
                  mode === 'import' ? `Import with ${parsedConfig.parcels.length} Parcels` : `Sync ${parsedConfig.parcels.length} Parcels`}
              </button>
            </div>
          </div>
        )}

        {step === 'done' && (
          <div className="py-8 text-center">
            <CheckCircle size={48} className="mx-auto mb-3 text-green-500" />
            <p className="text-lg font-bold text-slate-800">
              {mode === 'import' ? 'Property Imported' : 'Config Synced'}
            </p>
            <p className="text-sm text-slate-500 mt-1">
              {parsedConfig?.parcels?.length} parcels {mode === 'import' ? 'created' : 'synced'} for {parsedConfig?.property_name || farmId}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
